import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import type { ChatSummary } from '@/types/domain';
import { Avatar } from '@/components/atoms/Avatar';
import { Icon, type IconName } from '@/components/atoms';
import styles from './ChatList.module.css';

export interface ChatListProps {
  chats: ChatSummary[];
  /** F-170 B1.7 — when set, each row gets a trailing action button. */
  onArchive?: (chatId: number) => void;
  onUnarchive?: (chatId: number) => void;
}

export function ChatList({ chats, onArchive, onUnarchive }: ChatListProps): JSX.Element {
  const handleAction = (
    e: MouseEvent<HTMLButtonElement>,
    handler: (id: number) => void,
    id: number,
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    handler(id);
  };

  return (
    <div className={styles.list}>
      {chats.map((c) => {
        const actionIcon: IconName | null = onUnarchive
          ? 'archive-restore'
          : onArchive
            ? 'archive'
            : null;
        const actionLabel = onUnarchive ? 'Розархівувати' : 'Архівувати';
        const actionHandler = onUnarchive ?? onArchive;
        const peerName = c.is_anonymous ? 'Клієнт А' : c.peer_first_name;
        // F-170 B2.9 — dual-line layout:
        //   WITH order: primary = order.title (bold/accent),
        //               secondary = "{peer} · {status}".
        //   WITHOUT order: primary = "{peer} · {peer_title}",
        //                  secondary = last message snippet.
        const hasOrder = !!(c.order_id && c.active_order);
        const primary = hasOrder
          ? c.active_order?.title || 'Замовлення'
          : c.peer_title
            ? `${peerName} · ${c.peer_title}`
            : peerName;
        const secondary = hasOrder
          ? `${peerName} · ${c.active_order?.status_label || 'у роботі'}`
          : c.last_message_snippet;
        return (
          <div key={c.id} className={styles.rowWrap}>
            <Link to={`/chats/${c.id}`} className={styles.row}>
              <Avatar
                src={c.peer_avatar_url}
                name={c.is_anonymous ? 'Клієнт' : c.peer_first_name}
                presence={c.peer_presence}
              />
              <div className={styles.body}>
                <div className={styles.top}>
                  <span
                    className={hasOrder ? `${styles.name} ${styles.namePrimary}` : styles.name}
                  >
                    {primary}
                  </span>
                  <span className={styles.time}>
                    {c.last_message_at ? dayjs(c.last_message_at).format('HH:mm') : ''}
                  </span>
                </div>
                <div className={styles.snippet}>
                  <span>{secondary}</span>
                  {c.unread_count > 0 ? (
                    <span className={styles.unread}>{c.unread_count}</span>
                  ) : null}
                </div>
              </div>
            </Link>
            {actionIcon && actionHandler ? (
              <button
                type="button"
                className={styles.action}
                onClick={(e) => handleAction(e, actionHandler, c.id)}
                aria-label={actionLabel}
                title={actionLabel}
              >
                <Icon name={actionIcon} size={18} />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
